import type { AppNotification } from "../models/Notification/notificationTypes";

const WELCOME_SHOWN_THIS_SESSION_KEY = "sustell_welcome_shown_session";

export function isNotificationActive(
  notification: AppNotification,
  now: Date = new Date(),
): boolean {
  const publishTime = notification.publishAt.getTime();
  const nowTime = now.getTime();

  if (notification.expiresAt) {
    return publishTime <= nowTime && nowTime < notification.expiresAt.getTime();
  }

  return publishTime <= nowTime;
}

export function isNotificationActiveForUser(
  notification: AppNotification,
  lastLogin: Date | null,
  now: Date = new Date(),
): boolean {
  if (!isNotificationActive(notification, now)) {
    return false;
  }

  if (lastLogin === null) {
    return true;
  }

  return notification.publishAt.getTime() > lastLogin.getTime();
}

export function filterNotificationsForBell(
  notifications: AppNotification[],
  lastLogin: Date | null,
  now: Date = new Date(),
): AppNotification[] {
  const byPublishAtDesc = (a: AppNotification, b: AppNotification): number =>
    b.publishAt.getTime() - a.publishAt.getTime();

  return notifications
    .filter((n) => {
      if (!isNotificationActive(n, now)) return false;
      if (n.isWelcome) return true;
      return isNotificationActiveForUser(n, lastLogin, now);
    })
    .sort(byPublishAtDesc);
}

export function getWelcomeNotificationsForPopup(
  notifications: AppNotification[],
  now: Date = new Date(),
): AppNotification[] {
  const welcomeActive = notifications.filter(
    (n) => n.isWelcome && isNotificationActive(n, now),
  );

  if (welcomeActive.length === 0) {
    return [];
  }

  const alreadyShown =
    typeof window !== "undefined"
      ? sessionStorage.getItem(WELCOME_SHOWN_THIS_SESSION_KEY)
      : null;

  if (alreadyShown) {
    return [];
  }

  if (typeof window !== "undefined") {
    sessionStorage.setItem(WELCOME_SHOWN_THIS_SESSION_KEY, "true");
  }

  return welcomeActive.sort(
    (a, b) => b.publishAt.getTime() - a.publishAt.getTime(),
  );
}

export function resetWelcomeNotificationSession(): void {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem(WELCOME_SHOWN_THIS_SESSION_KEY);
  }
}
